import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BootScreen } from "./BootScreen.js";
import { starterBusinessModel, type AgentControlState, type AgentProposal, type BoardSnapshot, type WorkItem, type WorkStatus } from "@business-ops/domain";
import { api, ApiError, AUTH_REQUIRED_EVENT, type Session, type WhatsAppConnection, type WhatsAppConversationRule } from "./api.js";
import { AppShell, type AppView, type OpsWorkspaceMode } from "./components/shell.js";
import { BusinessTrialStatus } from "./components/business-trial-status.js";
import { BusinessFirstSetup } from "./components/business-first-setup.js";
import { ProductOnboarding } from "./components/product-onboarding.js";
import { ProductEntry, PublicMarketingRoute } from "./components/product-entry.js";
import { productApi, signOutWorkspace } from "./product-api.js";
import { AddWorkDrawer, WorkDetailDrawer } from "./components/work-forms.js";
import { ToastRegion, type ToastMessage } from "./components/ui.js";
import { opsEvidenceThreadReturnFromHistory, opsEvidenceVerificationFromUrl, type OpsEvidenceThreadReturnReceipt } from "./components/ops-assistant.js";
import { createTrailingRefresh, useBoard } from "./use-board.js";
import { approvalUnits, type AgentWorkQueue } from "./views/review.js";
import { OpsAgentView } from "./views/ops-agent.js";
import { canonicalActiveAgentProposals } from "./views/work.js";
import { ConversationsView } from "./views/conversations.js";
import { ActivityView, ClientsView, SettingsView } from "./views/secondary.js";
import { WhatsAppConnectionView } from "./views/whatsapp.js";
import { browserPrivateModeStorage, readPrivateMode, usePrivateModePresentation, writePrivateMode } from "./privacy-mode.js";
import { takeConfirmationFragment } from "./confirmation-link.js";
import { PreservedWorkspace } from "./preserved-workspace.js";
import { SummaryReader, summaryTurnFromUrl } from "./components/summary-reader.js";

// A cold email navigation must lose its credential-bearing fragment before
// the first React render or the ordinary session/route effects run. StrictMode
// may remount App, so capture once at module evaluation, not in an initializer.
const initialConfirmationLinkToken = typeof window === "undefined"
  ? null : takeConfirmationFragment(window.location, window.history);

export function App() {
  return <PublicMarketingRoute forceApp={initialConfirmationLinkToken !== null}><WorkspaceEntry /></PublicMarketingRoute>;
}

function WorkspaceEntry() {
  // Retain the requested immutable ID through the existing sign-in flow. It
  // never selects a workspace, supplies credentials or grants reader access.
  const [summaryTurnId] = useState(() => summaryTurnFromUrl(window.location.href));
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [requestedBusinessSlug, setRequestedBusinessSlug] = useState("");
  const [confirmationLinkToken, setConfirmationLinkToken] = useState<string | null>(initialConfirmationLinkToken);
  const confirmationDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const acceptLink = (previousHref?: string) => {
      const token = takeConfirmationFragment(window.location, window.history, previousHref);
      if (token !== null) setConfirmationLinkToken(token);
    };
    const onHashChange = (event: HashChangeEvent) => acceptLink(event.oldURL || undefined);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  useEffect(() => {
    void api.session().then(setSession).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) setSession(null);
      else setSession(null);
    });
  }, []);
  useEffect(() => {
    const dialog = confirmationDialogRef.current;
    if (!dialog) return;
    if (session !== null && confirmationLinkToken !== null && !dialog.open) dialog.showModal();
    else if (confirmationLinkToken === null && dialog.open) dialog.close();
    return () => { if (dialog.open) dialog.close(); };
  }, [session, confirmationLinkToken]);
  if (session === undefined) return <BootScreen />;
  if (session === null) return <ProductEntry
    initialBusinessSlug={requestedBusinessSlug}
    legacyLogin={(login, password) => api.login(login, password)}
    loadWorkspaceSession={() => api.session()}
    onLogin={(nextSession) => { setRequestedBusinessSlug(""); setConfirmationLinkToken(null); setSession(nextSession); }}
    {...(confirmationLinkToken !== null ? {
      confirmationLinkToken,
      alreadySignedIn: false,
      onLeaveConfirmation: () => setConfirmationLinkToken(null)
    } : {})}
  />;
  if (summaryTurnId && confirmationLinkToken === null) return <SummaryReader
    key={`${session.workspace.id}:${session.member.id}:${summaryTurnId}`}
    session={session} turnId={summaryTurnId} onAuthRequired={() => setSession(null)}
  />;
  return <PreservedWorkspace
    workspace={<AuthenticatedApp
      key={session.workspace.id + ":" + session.member.id}
      session={session}
      onSignedOut={() => { setRequestedBusinessSlug(""); setSession(null); }}
      onSwitchBusiness={(businessSlug) => { setRequestedBusinessSlug(businessSlug); setSession(null); }}
    />}
    confirmation={<dialog ref={confirmationDialogRef} className="product-confirm-dialog" aria-label="Email confirmation"
      onCancel={(event) => { event.preventDefault(); setConfirmationLinkToken(null); }}>
      {confirmationLinkToken !== null ? <ProductOnboarding
        initialBusinessSlug={requestedBusinessSlug}
        legacyLogin={(login, password) => api.login(login, password)}
        loadWorkspaceSession={() => api.session()}
        onLogin={(nextSession) => { setRequestedBusinessSlug(""); setConfirmationLinkToken(null); setSession(nextSession); }}
        confirmationLinkToken={confirmationLinkToken}
        alreadySignedIn
        onLeaveConfirmation={() => setConfirmationLinkToken(null)}
      /> : null}
    </dialog>}
  />;
}

type FirstUseAdmission = { readiness: "MISSING" | "ERROR" | "READY"; workspace: BoardSnapshot["workspace"] | null };

export async function admitFirstUseProfile(input: {
  workspaceId: string;
  canConfigure: boolean;
  client: Pick<typeof api, "businessModel" | "board" | "initializeStarterBusinessModel">;
  attempted: Set<string>;
  inFlight: Map<string, Promise<unknown>>;
}): Promise<FirstUseAdmission> {
  const { workspaceId, client, attempted, inFlight } = input;
  try {
    const current = await client.businessModel();
    return { readiness: current.workspaceId === workspaceId ? "READY" : "ERROR", workspace: null };
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 409 && error.code === "BUSINESS_MODEL_NOT_CONFIGURED")) {
      return { readiness: "ERROR", workspace: null };
    }
  }
  let workspace: BoardSnapshot["workspace"];
  try {
    const snapshot = await client.board();
    if (snapshot.workspace.id !== workspaceId) return { readiness: "ERROR", workspace: null };
    workspace = snapshot.workspace;
  } catch {
    return { readiness: "ERROR", workspace: null };
  }
  // WHY [BOL-FIRST-USE-ADMISSION-001]: missing profile used to strand normal
  // Chat and Voice behind a human Start click. Initialize only the canonical
  // unknown-facts starter for an authorized owner/manager. The server's
  // workspace, version-0 CAS, audit and policy guard remain authoritative.
  // Every outcome, including a 409 or uncertain delivery, is reread; never
  // auto-retry the mutation on focus or turn a non-missing error into setup.
  if (input.canConfigure && !attempted.has(workspaceId)) {
    attempted.add(workspaceId);
    try {
      inFlight.set(workspaceId, client.initializeStarterBusinessModel(
        starterBusinessModel(workspace.name, workspace.timezone)
      ));
    } catch {
      // A malformed canonical identity cannot become a guessed profile or
      // leave the workspace indefinitely "Checking business setup."
      return { readiness: "ERROR", workspace };
    }
  }
  const pending = inFlight.get(workspaceId);
  if (!pending) return { readiness: "MISSING", workspace };
  try { await pending; } catch { /* Canonical reread decides committed state. */ }
  if (inFlight.get(workspaceId) === pending) inFlight.delete(workspaceId);
  try {
    const current = await client.businessModel();
    return { readiness: current.workspaceId === workspaceId ? "READY" : "ERROR", workspace };
  } catch (error) {
    return { readiness: error instanceof ApiError && error.status === 409
      && error.code === "BUSINESS_MODEL_NOT_CONFIGURED" ? "MISSING" : "ERROR", workspace };
  }
}

function AuthenticatedApp({ session, onSignedOut, onSwitchBusiness }: {
  session: Session;
  onSignedOut: () => void;
  onSwitchBusiness: (businessSlug: string) => void;
}) {
  const [readiness, setReadiness] = useState<"LOADING" | "MISSING" | "ERROR" | "READY">("LOADING");
  const [setupWorkspace, setSetupWorkspace] = useState<BoardSnapshot["workspace"] | null>(null);
  const [setupRoute, setSetupRoute] = useState(() => !window.location.hash
    || appViewFromHash(window.location.hash) === "whatsapp" ? "whatsapp" : "profile");
  const [setupMessages, setSetupMessages] = useState<ToastMessage[]>([]);
  const requestId = useRef(0);
  const mounted = useRef(false);
  const starterAttempted = useRef(new Set<string>());
  const starterInFlight = useRef(new Map<string, Promise<unknown>>());
  const canConfigure = session.member.role === "OWNER" || session.member.role === "MANAGER";
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const notifySetup = useCallback((text: string, tone: "success" | "error" = "success") => {
    const id = Date.now() + Math.floor(Math.random() * 1_000);
    setSetupMessages((current) => [...current.slice(-2), { id, text, tone }]);
  }, []);
  const checkProfile = useCallback(async () => {
    const request = ++requestId.current;
    const result = await admitFirstUseProfile({ workspaceId: session.workspace.id, canConfigure,
      client: api, attempted: starterAttempted.current, inFlight: starterInFlight.current });
    if (!mounted.current || request !== requestId.current) return;
    setSetupWorkspace(result.workspace);
    setReadiness(result.readiness);
  }, [canConfigure, session.workspace.id]);
  useEffect(() => {
    mounted.current = true;
    void checkProfile();
    return () => { mounted.current = false; requestId.current++; };
  }, [checkProfile]);
  useEffect(() => {
    // A configured workspace keeps its mounted Chat, history and Voice. Only
    // blocked entry rechecks on return, including setup completed in another tab.
    if (readiness === "READY") return;
    const refresh = () => { void checkProfile(); };
    const route = () => setSetupRoute(!window.location.hash
      || appViewFromHash(window.location.hash) === "whatsapp" ? "whatsapp" : "profile");
    const requireAuth = () => onSignedOut();
    window.addEventListener("focus", refresh);
    window.addEventListener("hashchange", route);
    window.addEventListener("popstate", route);
    window.addEventListener(AUTH_REQUIRED_EVENT, requireAuth);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("hashchange", route);
      window.removeEventListener("popstate", route);
      window.removeEventListener(AUTH_REQUIRED_EVENT, requireAuth);
    };
  }, [checkProfile, onSignedOut, readiness]);
  if (readiness === "READY") return <ReadyAuthenticatedApp
    session={session} onSignedOut={onSignedOut} onSwitchBusiness={onSwitchBusiness} />;
  if (readiness === "LOADING") return <BootScreen label="Checking business setup." />;

  // WHY [BOL-FIRST-SETUP-ENTRY-001]: an empty Board is valid before first setup,
  // so gating only on Board mounted Chat/Voice without a business profile. Both
  // then failed (Voice before provider.connect). Only the canonical missing-model
  // 409 opens the existing form; other failures never invent a new business.
  // The workspace/member key above and request fence reject stale identity reads.
  // Saving or accepting a concurrent profile rereads the canonical owner before
  // mounting the normal workspace. WhatsApp connection-first remains accessible
  // independently: bare signup entry/#whatsapp uses its unchanged view, whose
  // CONNECTED #settings link opens this form. Gating the entire entry previously
  // blocked that accepted prerequisite; no new QR/status/protocol owner is needed.
  // Falsifier: cold/reload/Back/direct-route entry reaches Chat without a profile,
  // a failed/wrong-workspace check unlocks it, a ready workspace remounts Voice,
  // or a missing profile blocks WhatsApp connection/reconnect or its setup link.
  const afterProfile = (model: { workspaceId: string }) => {
    if (!mounted.current || model.workspaceId !== session.workspace.id) return;
    setReadiness("LOADING");
    void checkProfile();
  };
  const logout = async () => {
    try {
      const kind = await signOutWorkspace({ apiClient: productApi, legacyLogout: () => api.logout() });
      onSignedOut();
      if (kind === "PRODUCT") window.location.replace("/");
    } catch {
      if (mounted.current) setSignOutError("Sign-out could not finish. Try again.");
    }
  };
  if (setupRoute === "whatsapp" && canConfigure) return <main className="settings-view" aria-label="Connect WhatsApp">
    <WhatsAppConnectionView notify={notifySetup} workspaceId={session.workspace.id} isOwner={session.member.role === "OWNER"} />
    {signOutError ? <p role="alert">{signOutError}</p> : null}
    <button className="secondary-button" type="button" onClick={() => void logout()}>Sign out</button>
    <ToastRegion messages={setupMessages} dismiss={(id) => setSetupMessages((current) => current.filter((message) => message.id !== id))} />
  </main>;
  return <main className="settings-view" aria-label="Business setup">
    {readiness === "ERROR" || !setupWorkspace ? <section className="first-setup__notice" role="alert">
      <h2>Business profile unavailable</h2>
      <p>We could not check the saved profile. No setup or change has been made.</p>
      <button className="secondary-button" type="button" onClick={() => void checkProfile()}>Retry profile check</button>
    </section> : canConfigure
      ? <BusinessFirstSetup workspaceName={setupWorkspace.name} workspaceTimezone={setupWorkspace.timezone}
          onCreated={afterProfile} onExisting={afterProfile} />
      : <section className="first-setup__notice" role="status">
          <h2>Business profile not set up</h2>
          <p>An owner or manager must enter this business’s operating details before Luna can use them.</p>
          <button className="secondary-button" type="button" onClick={() => void checkProfile()}>Check setup</button>
        </section>}
    {signOutError ? <p role="alert">{signOutError}</p> : null}
    {canConfigure ? <a className="secondary-button" href="#whatsapp">Connect WhatsApp</a> : null}
    <button className="secondary-button" type="button" onClick={() => void logout()}>Sign out</button>
  </main>;
}

function ReadyAuthenticatedApp({ session, onSignedOut, onSwitchBusiness }: {
  session: Session;
  onSignedOut: () => void;
  onSwitchBusiness: (businessSlug: string) => void;
}) {
  const privacyScope = { workspaceId: session.workspace.id, memberId: session.member.id };
  const [privateMode, setPrivateMode] = useState(() => readPrivateMode(browserPrivateModeStorage(), privacyScope));
  const [voicePresentationPrivacyLocked, setVoicePresentationPrivacyLocked] = useState(false);
  const [trialEnded, setTrialEnded] = useState(false);
  const voicePresentationPrivacyLockedRef = useRef(false);
  const [view, setViewState] = useState<AppView>(() => viewFromHash());
  const [opsMode, setOpsModeState] = useState<OpsWorkspaceMode>(() => opsWorkspaceModeFromHash(window.location.hash));
  const [mobileChatOpen, setMobileChatOpen] = useState(() => appMobileChatFromHistoryState(window.history.state));
  const [whatsAppSourceEventId, setWhatsAppSourceEventId] = useState<string | null>(
    () => whatsAppSourceFromUrl(window.location.href)
  );
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const [selected, setSelected] = useState<WorkItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [addWorkDueDay, setAddWorkDueDay] = useState<string | null>(null);
  const [agentProposals, setAgentProposals] = useState<AgentProposal[]>([]);
  const [agentProposalHistory, setAgentProposalHistory] = useState<AgentProposal[]>([]);
  const [agentQueueLoadState, setAgentQueueLoadState] = useState<"LOADING" | "READY" | "STALE">("LOADING");
  const [agentControl, setAgentControl] = useState<AgentControlState | null>(null);
  const [agentConversations, setAgentConversations] = useState<WhatsAppConversationRule[]>([]);
  const [whatsAppConnectionStatus, setWhatsAppConnectionStatus] = useState<WhatsAppConnection["status"] | null>(null);
  usePrivateModePresentation(privateMode);
  const handleVoicePresentationPrivacyLockChange = useCallback((locked: boolean) => {
    voicePresentationPrivacyLockedRef.current = locked;
    setVoicePresentationPrivacyLocked(locked);
  }, []);
  const requestPrivateMode = useCallback((enabled: boolean) => {
    // The ref closes the event-to-render gap: both visible controls are also
    // disabled, but this owner rejects a same-tick click before React repaints.
    if (voicePresentationPrivacyLockedRef.current) return;
    setPrivateMode(enabled);
  }, []);
  useEffect(() => {
    writePrivateMode(browserPrivateModeStorage(), privacyScope, privateMode);
  }, [privacyScope.memberId, privacyScope.workspaceId, privateMode]);
  useEffect(() => {
    const requireAuth = () => onSignedOut();
    window.addEventListener(AUTH_REQUIRED_EVENT, requireAuth);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, requireAuth);
  }, [onSignedOut]);
  useEffect(() => {
    // WHY [BOL-OPS-HISTORY-RESTORE-001]: phone tools and Chat are two visual
    // states of one mounted workspace. Browser Back must restore the exact
    // prior component instance (filters, focus, scroll and draft), not infer a
    // fresh screen from whichever hash happens to remain. Falsifier: Back from
    // Ask Agent exits the app, remounts a tool, or leaves shell and URL apart.
    const restoreRoute = (event: PopStateEvent) => {
      const route = appRouteFromUrl(window.location.href);
      setViewState(route.view);
      setOpsModeState(route.opsMode);
      setWhatsAppSourceEventId(route.whatsAppSourceEventId);
      setMobileChatOpen(appMobileChatFromHistoryState(event.state));
    };
    window.addEventListener("popstate", restoreRoute);
    return () => window.removeEventListener("popstate", restoreRoute);
  }, []);
  useEffect(() => {
    // A task-specific link may be opened from Chat while this app is mounted.
    // Native hashchange is distinct from our pushState route transitions.
    const openDetails = () => {
      if (window.location.hash === "#settings/business-details" || window.location.hash === "#settings/subscription") setViewState("settings");
      if (window.location.hash === "#settings/subscription") {
        // Match normal navigation out of mobile Chat without pushing another
        // history entry; Back remains owned by the existing popstate handler.
        setMobileChatOpen(false);
        setWhatsAppSourceEventId(null);
      }
    };
    window.addEventListener("hashchange", openDetails);
    return () => window.removeEventListener("hashchange", openDetails);
  }, []);
  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    const id = Date.now() + Math.floor(Math.random() * 1_000);
    setMessages((current) => [...current.slice(-2), { id, text, tone }]);
    window.setTimeout(() => setMessages((current) => current.filter((message) => message.id !== id)), 4_500);
  }, []);
  const canConfigureAutomation = session.member.role === "OWNER" || session.member.role === "MANAGER";
  useEffect(() => {
    if (!canConfigureAutomation) {
      setWhatsAppConnectionStatus(null);
      return;
    }
    let active = true;
    const refreshConnection = () => {
      void api.whatsAppConnection()
        .then((connection) => { if (active) setWhatsAppConnectionStatus(connection.status); })
        .catch(() => { if (active) setWhatsAppConnectionStatus("UNAVAILABLE"); });
    };
    refreshConnection();
    // WHY [BOL-WHATSAPP-LIVE-SEND-GATE-001]: Manual approval must never look
    // sendable while the isolated Baileys socket is waiting for a QR scan. The
    // final server/connector guard remains authoritative; this low-rate status
    // refresh only prevents the exact frozen-click experience reported by the
    // operator. Do not infer readiness from saved conversations or transport
    // policy. Falsifier: an immediate Send stays enabled after SCAN_REQUIRED,
    // or remains locked more than one refresh after CONNECTED.
    const timer = window.setInterval(refreshConnection, 15_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [canConfigureAutomation]);
  const runAgentQueueRefresh = useCallback(async () => {
    if (!canConfigureAutomation) {
      setAgentProposals([]);
      setAgentProposalHistory([]);
      setAgentQueueLoadState("READY");
      setAgentControl(null);
      setAgentConversations([]);
      return;
    }
    setAgentQueueLoadState((current) => current === "STALE" ? "LOADING" : current);
    // WHY [BOL-ACTION-PROJECTION-LOAD-001]: an empty approval queue is a
    // business fact, while a pending/failed request is not. Loading Review,
    // controls, and the channel directory as one Promise.all previously made
    // any slow or failed companion request briefly erase real prepared work.
    // Keep the last proven actions, expose loading/stale truth to Work, and let
    // each optional projection recover independently. This boundary remains
    // channel-neutral when Instagram/Threads adapters join WhatsApp.
    // Falsifier: the owner sees "Nothing needs your approval" while /api/review
    // is pending/failing or because an unrelated adapter/control request fails.
    const reviewLoad = api.review().then((review) => {
      setAgentProposals(review.proposals);
      setAgentProposalHistory(review.history);
      setAgentQueueLoadState("READY");
    }).catch(() => {
      setAgentQueueLoadState("STALE");
    });
    const controlLoad = api.agentControl().then(setAgentControl).catch(() => undefined);
    const directoryLoad = api.whatsAppConversations()
      .then((whatsApp) => setAgentConversations(whatsApp.conversations))
      .catch(() => undefined);
    await Promise.all([reviewLoad, controlLoad, directoryLoad]);
  }, [canConfigureAutomation]);
  // WHY [BOL-ACTION-PROJECTION-TRAILING-001]: editing a scheduled message
  // commits a new Manual-review revision while SSE and the explicit refresh can
  // overlap. Unordered Review reads let an older response overwrite the newer
  // one, hiding reapproval behind an obsolete Sent card. Serialize bursts and
  // always finish with one trailing canonical read. Falsifier: Save for review
  // requires a page reload before the revised schedule appears.
  const refreshAgentQueue = useMemo(
    () => createTrailingRefresh(runAgentQueueRefresh),
    [runAgentQueueRefresh]
  );
  const boardController = useBoard(useCallback((message: string) => notify(message, "error"), [notify]), refreshAgentQueue);
  const canonicalAgentProposals = useMemo(
    () => boardController.board
      ? canonicalActiveAgentProposals(boardController.board, agentProposals)
      : [],
    [agentProposals, boardController.board]
  );
  useEffect(() => { void refreshAgentQueue(); }, [refreshAgentQueue]);
  const canArchiveWork = session.member.role === "OWNER" || session.member.role === "MANAGER";
  const activeView = ["conversations", "whatsapp"].includes(view) && !canConfigureAutomation ? "ops" : view;

  const setView = (next: AppView) => {
    setViewState(next);
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(null);
    pushAppRoute(next === "ops" ? opsModeHash(opsMode) : `#${next}`);
  };
  const openConversations = (sourceEventId?: string, returnReceipt?: OpsEvidenceThreadReturnReceipt | null) => {
    const exactSource = normalizeSourceEventId(sourceEventId ?? null);
    // WHY [BOL-OPS-SOURCE-COVERAGE-RETURN-001]: lazy coverage links enter the
    // conversation directory without a proof URL, so the existing proof-only
    // return marker was absent and the human had no visible Back to chat.
    // Preserve only the exact originating turn/scroll receipt supplied by the
    // mounted Chat thread; direct Conversations entries remain ordinary.
    // Falsifier: coverage opens the wrong turn/latest Chat, invents a claim or
    // proof route, or direct/conversation navigation gains a false origin.
    const sourceReturn = returnReceipt ?? null;
    const evidenceReturn = sourceReturn ?? opsEvidenceThreadReturnFromHistory(window.history.state);
    const openedFromOpsProof = Boolean(
      opsEvidenceVerificationFromUrl(window.location.href)
      && evidenceReturn
      && appRouteFromUrl(window.location.href).view !== "conversations"
    );
    const continuesOpsProofDrilldown = Boolean(
      sourceReturn
      || (evidenceReturn
        && (openedFromOpsProof || appOpsChatReturnAvailable(window.location.href, window.history.state))
      )
    );
    setViewState("conversations");
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(exactSource);
    pushAppRoute(
      "#conversations",
      exactSource,
      {
        historyState: sourceReturn
          ? appHistoryStateForSourceConversation(window.history.state, sourceReturn)
          : continuesOpsProofDrilldown
            ? appHistoryStateForConversation(window.history.state, true)
            : window.history.state,
        preserveOpsEvidenceDrilldown: !sourceReturn && continuesOpsProofDrilldown,
        preserveOpsChatReturn: Boolean(sourceReturn)
      }
    );
  };
  const setOpsMode = (next: OpsWorkspaceMode) => {
    // WHY [BOL-OPS-SURFACE-ROUTE-001]: Board, Calendar, and Approvals are
    // interchangeable views over one live Ops workspace. Preserve the mounted
    // conversation while making each surface directly linkable. Falsifier: a
    // tool click remounts chat, clears its draft, or opens a second task state.
    setViewState("ops");
    setOpsModeState(next);
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(null);
    pushAppRoute(opsModeHash(next));
  };
  const openMobileChat = () => {
    if (mobileChatOpen) return;
    setMobileChatOpen(true);
    pushMobileChatRoute();
  };
  const openAddWork = (dueDay: string | null = null) => {
    setAddWorkDueDay(dueDay);
    setAdding(true);
  };
  const closeAddWork = () => {
    setAdding(false);
    setAddWorkDueDay(null);
  };
  const moveWork = async (item: WorkItem, status: WorkStatus) => {
    try { await boardController.updateWork(item, { status }); notify(`Moved to ${status.toLowerCase().replaceAll("_", " ")}.`); }
    catch (error) { notify(error instanceof Error ? error.message : "That move did not save.", "error"); }
  };
  const logout = async () => {
    try {
      const kind = await signOutWorkspace({ apiClient: productApi, legacyLogout: () => api.logout() });
      onSignedOut();
      if (kind === "PRODUCT") window.location.replace("/");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Sign-out could not finish safely. Please try again.", "error");
    }
  };
  const agentQueue: AgentWorkQueue = {
    proposals: canonicalAgentProposals,
    history: agentProposalHistory,
    loadState: agentQueueLoadState,
    refresh: refreshAgentQueue,
    conversations: agentConversations,
    transportMode: agentControl?.transportMode ?? "BLOCKED",
    whatsAppConnectionStatus,
    openWhatsApp: () => setView("whatsapp"),
    notify,
    removeProposal: (id) => setAgentProposals((current) => current.filter((proposal) => proposal.id !== id)),
    replaceProposal: (updated) => setAgentProposals((current) => current.map((proposal) => proposal.id === updated.id ? updated : proposal)),
    replaceConversation: (updated) => setAgentConversations((current) => current.map((conversation) => conversation.conversationId === updated.conversationId ? updated : conversation)),
    afterBoardChange: async () => {
      await Promise.all([boardController.refresh(), refreshAgentQueue()]);
    }
  };

  if (boardController.loading && !boardController.board) return <BootScreen label="Loading board." />;
  if (!boardController.board) return <FailureScreen retry={() => void boardController.refresh()} />;
  const board = boardController.board;

  return (
    <>
      {/* WHY [BOL-SELF-SERVE-WEB-004]: the desk shows the server's trial phase in
          the desktop top bar (paid, Agency, Rentals and beta businesses render
          nothing). The WhatsApp status refresh key rereads it when the first
          connection may have started the clock. */}
      <AppShell session={session} view={mobileChatOpen ? "ops" : activeView} setView={setView} opsMode={mobileChatOpen ? "today" : opsMode} setOpsMode={setOpsMode} logout={() => void logout()} connection={boardController.loading ? "refreshing" : "live"} actionCount={approvalCountForQueue(canonicalAgentProposals)} privateMode={privateMode} privateModeLocked={voicePresentationPrivacyLocked} setPrivateMode={requestPrivateMode}
        whatsAppStatus={whatsAppConnectionStatus}
        trialEnded={trialEnded}
        notice={<BusinessTrialStatus variant="topbar" businessTimezone={board.workspace.timezone} refreshKey={whatsAppConnectionStatus ?? "UNKNOWN"} onEndedChange={setTrialEnded} {...(canConfigureAutomation ? { openWhatsApp: () => setView("whatsapp") } : {})} />}>
        {activeView === "ops" ? <OpsAgentView
          board={board}
          whatsAppConnectionStatus={whatsAppConnectionStatus}
          {...(canConfigureAutomation ? { openWhatsApp: () => setView("whatsapp") } : {})}
          queue={agentQueue}
          historyScope={`${session.workspace.id}:${session.member.id}`}
          publicReleaseId={session.publicReleaseId}
          privateMode={privateMode}
          onVoicePresentationPrivacyLockChange={handleVoicePresentationPrivacyLockChange}
          canArchiveWork={canArchiveWork}
          displayName={session.member.displayName}
          mode={opsMode}
          setMode={setOpsMode}
          openWork={setSelected}
          moveWork={moveWork}
          addWork={() => openAddWork()}
          addWorkForDay={(dayKey) => openAddWork(dayKey)}
          openConversations={canConfigureAutomation ? openConversations : undefined}
          mobileChatOpen={mobileChatOpen}
          openMobileChat={openMobileChat}
        /> : null}
        {activeView === "clients" ? <ClientsView board={board} openWork={setSelected} /> : null}
        {activeView === "activity" ? <ActivityView afterUndo={boardController.refresh} notify={notify} /> : null}
        {activeView === "conversations" ? <ConversationsView
          focusSourceEventId={whatsAppSourceEventId}
          focusSource={openConversations}
          clearFocusSource={() => openConversations()}
          {...(appOpsChatReturnAvailable(window.location.href, window.history.state)
            ? { returnToOpsChat: () => {
                if (!returnToOpsChatFromHistory()) return;
                setViewState("ops");
                setOpsModeState("today");
                setMobileChatOpen(false);
                setWhatsAppSourceEventId(null);
              } }
            : {})}
          openWork={(workItemId) => {
            const workItem = board.workItems.find((candidate) => candidate.id === workItemId);
            if (workItem) {
              setSelected(workItem);
              return;
            }
            setOpsMode("board");
            void boardController.refresh();
            notify("Task is refreshing. Open it from the board.");
          }}
          openApprovals={() => setOpsMode("approvals")}
        /> : null}
        {activeView === "whatsapp" ? <WhatsAppConnectionView notify={notify} workspaceId={session.workspace.id} isOwner={session.member.role === "OWNER"} /> : null}
        {activeView === "settings" ? <SettingsView board={board} canConfigure={canConfigureAutomation} notify={notify} openWhatsApp={canConfigureAutomation ? () => setView("whatsapp") : undefined} afterProposalRestore={refreshAgentQueue} privateMode={privateMode} setPrivateMode={requestPrivateMode} onSwitchBusiness={onSwitchBusiness} /> : null}
      </AppShell>
      <AddWorkDrawer open={adding} initialDueDay={addWorkDueDay} board={board} close={closeAddWork} create={boardController.createWork} notify={notify} />
      <WorkDetailDrawer selected={selected} board={board} canArchive={canArchiveWork} close={() => setSelected(null)} update={boardController.updateWork} notify={notify} agentQueue={agentQueue} />
      <ToastRegion messages={messages} dismiss={(id) => setMessages((current) => current.filter((message) => message.id !== id))} />
    </>
  );
}

export function approvalCountForQueue(proposals: readonly AgentProposal[]): number {
  // WHY [BOL-APPROVAL-COUNT-001]: one manager decision can contain a Task and
  // its linked Message while both effects stay separately audited. Count the
  // same complete review units the operator browses, never raw proposal rows.
  // Falsifier: one Task + Message bundle shows as two actions in the shell.
  return approvalUnits(proposals).length;
}


function FailureScreen({ retry }: { retry: () => void }) {
  return <main className="boot-screen"><h1>The board could not be loaded.</h1><p>Check the connection and try again.</p><button className="primary-button" type="button" onClick={retry}>Try again</button></main>;
}

function viewFromHash(): AppView {
  return appViewFromHash(window.location.hash);
}

const SOURCE_EVENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function normalizeSourceEventId(value: string | null): string | null {
  return value && SOURCE_EVENT_ID_PATTERN.test(value) ? value.toLocaleLowerCase() : null;
}

export function whatsAppSourceFromUrl(value: string): string | null {
  try {
    return normalizeSourceEventId(new URL(value, "https://business-ops.invalid").searchParams.get("source"));
  } catch {
    return null;
  }
}

const MOBILE_CHAT_HISTORY_KEY = "businessOpsMobileChat";
const OPS_CHAT_RETURN_HISTORY_KEY = "businessOpsChatReturn";
const OPS_CHAT_RETURN_HISTORY_SCHEMA = "ops.chat-return.v1";
const OPS_EVIDENCE_HISTORY_KEYS = [
  "businessOpsEvidenceAnchor",
  "businessOpsEvidenceProof",
  "businessOpsEvidenceReturn",
  OPS_CHAT_RETURN_HISTORY_KEY
] as const;

export function appMobileChatFromHistoryState(value: unknown): boolean {
  return Boolean(value && typeof value === "object" && !Array.isArray(value)
    && (value as Record<string, unknown>)[MOBILE_CHAT_HISTORY_KEY] === true);
}

export function appHistoryStateWithMobileChat(value: unknown, open: boolean): Record<string, unknown> {
  const prior = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  return { ...prior, [MOBILE_CHAT_HISTORY_KEY]: open };
}

export function appHasOpsChatReturnMarker(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = (value as Record<string, unknown>)[OPS_CHAT_RETURN_HISTORY_KEY];
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return false;
  const record = candidate as Record<string, unknown>;
  return record.schema === OPS_CHAT_RETURN_HISTORY_SCHEMA;
}

export function appHistoryStateForConversation(value: unknown, retainOpsChatReturn: boolean): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  if (retainOpsChatReturn) {
    next[OPS_CHAT_RETURN_HISTORY_KEY] = { schema: OPS_CHAT_RETURN_HISTORY_SCHEMA };
  } else {
    delete next[OPS_CHAT_RETURN_HISTORY_KEY];
  }
  return next;
}

export function appHistoryStateForSourceConversation(
  value: unknown,
  returnReceipt: OpsEvidenceThreadReturnReceipt
): Record<string, unknown> {
  return appHistoryStateForConversation({
    ...appHistoryStateWithoutOpsEvidenceDrilldown(value),
    businessOpsEvidenceReturn: returnReceipt
  }, true);
}

export function appHistoryStateWithoutOpsEvidenceDrilldown(value: unknown): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  for (const key of OPS_EVIDENCE_HISTORY_KEYS) delete next[key];
  return next;
}

interface OpsChatHistoryTarget {
  location: Pick<Location, "href">;
  history: Pick<History, "state" | "pushState">;
}

export function appOpsChatReturnAvailable(value: string, state: unknown): boolean {
  const receipt = opsEvidenceThreadReturnFromHistory(state);
  if (!receipt) return false;
  if (appHasOpsChatReturnMarker(state)) return true;
  const proof = opsEvidenceVerificationFromUrl(value);
  const sourceEventId = whatsAppSourceFromUrl(value);
  // Upgrade recovery for a source page opened before the return marker existed:
  // the content-free proof route, exact source, and receipt must all agree.
  return appRouteFromUrl(value).view === "conversations"
    && proof !== null
    && sourceEventId === proof.sourceEventId
    && receipt.turnId === proof.turnId;
}

function appHistoryStateForOpsChatReturn(value: unknown): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  delete next.businessOpsEvidenceAnchor;
  delete next.businessOpsEvidenceProof;
  delete next[OPS_CHAT_RETURN_HISTORY_KEY];
  return next;
}

export function returnToOpsChatFromHistory(
  target: OpsChatHistoryTarget | null = typeof window === "undefined" ? null : window
): boolean {
  if (!target || !appOpsChatReturnAvailable(target.location.href, target.history.state)) return false;
  // WHY [BOL-OPS-SOURCE-RETURN-001]: the source entry already owns the exact
  // content-free Chat turn/scroll receipt. Route directly to canonical Chat
  // with that receipt instead of guessing how many proof/list history entries
  // precede it. Keep the receipt reusable for Back/Forward; unrelated pushes
  // strip it below. Falsifier: Back to chat opens proof/the directory, jumps to
  // latest after Forward/reload, or appears on direct Conversations entry.
  target.history.pushState(
    appHistoryStateForOpsChatReturn(target.history.state),
    "",
    appPathForRoute(target.location.href, "#ops", null, false)
  );
  return true;
}

export interface AppRouteState {
  view: AppView;
  opsMode: OpsWorkspaceMode;
  whatsAppSourceEventId: string | null;
}

export function appRouteFromUrl(value: string): AppRouteState {
  const url = new URL(value, "https://business-ops.invalid");
  const view = appViewFromHash(url.hash);
  return {
    view,
    opsMode: opsWorkspaceModeFromHash(url.hash),
    whatsAppSourceEventId: view === "conversations" ? normalizeSourceEventId(url.searchParams.get("source")) : null
  };
}

export function appPathForRoute(
  value: string,
  hash: string,
  sourceEventId: string | null,
  preserveOpsEvidenceDrilldown: boolean
): string {
  const url = new URL(value, "https://business-ops.invalid");
  if (!preserveOpsEvidenceDrilldown) {
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("opsProof")) url.searchParams.delete(key);
    }
  }
  if (sourceEventId) url.searchParams.set("source", sourceEventId);
  else url.searchParams.delete("source");
  url.hash = hash;
  return `${url.pathname}${url.search}${url.hash}`;
}

function pushAppRoute(hash: string, sourceEventId: string | null = null, options: {
  historyState?: unknown;
  preserveOpsEvidenceDrilldown?: boolean;
  preserveOpsChatReturn?: boolean;
} = {}): void {
  const preserveOpsEvidenceDrilldown = options.preserveOpsEvidenceDrilldown === true;
  const next = appPathForRoute(
    window.location.href,
    hash,
    sourceEventId,
    preserveOpsEvidenceDrilldown
  );
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  // Proof-return receipts stay on the original summary and its matching
  // drilldown only. Sanitizing the newly pushed unrelated entry does not alter
  // that earlier R50 owner, so browser Back can still restore its turn/scroll.
  // A coverage source has a Chat origin but no semantic proof route. Keep
  // that return receipt while stripping any stale proof URL parameters.
  const nextState = preserveOpsEvidenceDrilldown || options.preserveOpsChatReturn === true
    ? appHistoryStateWithMobileChat(options.historyState ?? window.history.state, false)
    : appHistoryStateWithoutOpsEvidenceDrilldown(options.historyState ?? window.history.state);
  if (next === current && !appMobileChatFromHistoryState(window.history.state)) return;
  window.history.pushState(nextState, "", next);
}

function pushMobileChatRoute(): void {
  window.history.pushState(
    appHistoryStateWithMobileChat(window.history.state, true),
    "",
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

export function appViewFromHash(hash: string): AppView {
  const candidate = hash.replace(/^#/, "");
  if (candidate === "settings/business-details" || candidate === "settings/subscription") return "settings";
  // WHY [BOL-ONE-WORKSPACE-001]: Board and Calendar are tools inside the
  // persistent Ops Agent workspace. Legacy bookmarks must enter that shell,
  // not remount the chat as a separate page and lose the operator's context.
  // Falsifier: #work or #calendar renders a standalone daily destination.
  if (["", "overview", "today", "review", "approvals", "work", "board", "calendar"].includes(candidate)) return "ops";
  return ["ops", "clients", "activity", "conversations", "whatsapp", "settings"].includes(candidate) ? candidate as AppView : "ops";
}

export function opsWorkspaceModeFromHash(hash: string): OpsWorkspaceMode {
  const candidate = hash.replace(/^#/, "");
  if (candidate === "calendar") return "calendar";
  if (candidate === "work" || candidate === "board") return "board";
  if (candidate === "review" || candidate === "approvals") return "approvals";
  return "today";
}

function opsModeHash(mode: OpsWorkspaceMode): string {
  return mode === "today" ? "#ops" : `#${mode}`;
}
